import React, { useEffect, useId, useRef, useState } from 'react';
import Button from './Button';
import DetailIcon from './DetailIcon';

// `paper` = it sits on a krit-paper card rather than the white page.
export default function ExpandableContent({ children, moreLabel, lessLabel, collapsedHeight = 224, paper = false }) {
  const contentId = useId();
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const collapsed = overflows && !expanded;

  useEffect(() => {
    const content = contentRef.current;
    const measure = () => setOverflows(content.getBoundingClientRect().height > collapsedHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [collapsedHeight]);

  const toggle = () => {
    setExpanded(!expanded);
    if (expanded && containerRef.current.getBoundingClientRect().top < 0) {
      containerRef.current.scrollIntoView({
        block: 'start',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }
  };

  return <div ref={containerRef} className="scroll-mt-28">
    <div id={contentId} className="relative" style={collapsed ? { maxHeight: collapsedHeight, overflow: 'clip' } : undefined}
      onFocusCapture={() => { if (collapsed) setExpanded(true); }}>
      <div ref={contentRef} className="flow-root">{children}</div>
      {collapsed && <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t ${paper ? 'from-krit-paper' : 'from-white'} to-transparent`} />}
    </div>
    {overflows && <Button variant="ghost" onClick={toggle} aria-expanded={expanded} aria-controls={contentId}
      className={`mt-3 min-h-[44px] gap-2 ${paper ? 'bg-white' : 'bg-krit-paper'} text-krit-ink`}>
      {expanded ? lessLabel : moreLabel}
      <DetailIcon name="chevronRight" filled className={`h-4 w-4 ${expanded ? '-rotate-90' : 'rotate-90'}`} />
    </Button>}
  </div>;
}
